import { createContext, useContext, useState, ReactNode, useCallback } from "react";

// Levenshtein distance calculation for arrays (word-level comparison)
function levenshteinDistanceArray<T>(arr1: T[], arr2: T[]): number {
  const m = arr1.length;
  const n = arr2.length;

  // Create a 2D array to store distances
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  // Initialize base cases
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  // Fill the DP table
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (arr1[i - 1] === arr2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }

  return dp[m][n];
}

// Split text into words for comparison
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\s+/)
    .filter(word => word.length > 0);
}

export type ChangeType =
  | "minor_wording"
  | "factual_error"
  | "tone_style"
  | "added_info"
  | "removed_info"
  | "structural";

export interface EmailFeedbackData {
  qualityScore: number;
  changeTypes: ChangeType[];
  comments: string;
  originalEmail: string;
  finalEmail: string;
}

interface EmailFeedbackContextType {
  // Track original vs current email
  originalEmail: string;
  currentEmail: string;
  setOriginalEmail: (email: string) => void;
  setCurrentEmail: (email: string) => void;

  // Change detection
  hasChanges: boolean;
  getQualityScore: () => number;

  // Feedback popup control
  showFeedbackPopup: boolean;
  setShowFeedbackPopup: (show: boolean) => void;
  pendingNavigation: (() => void) | null;
  setPendingNavigation: (fn: (() => void) | null) => void;

  // Reset state (when changing candidates)
  resetFeedbackState: () => void;
}

const EmailFeedbackContext = createContext<EmailFeedbackContextType | undefined>(
  undefined
);

export function EmailFeedbackProvider({ children }: { children: ReactNode }) {
  const [originalEmail, setOriginalEmailState] = useState("");
  const [currentEmail, setCurrentEmailState] = useState("");
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(
    null
  );

  const setOriginalEmail = useCallback((email: string) => {
    setOriginalEmailState(email);
  }, []);

  const setCurrentEmail = useCallback((email: string) => {
    setCurrentEmailState(email);
  }, []);

  const hasChanges = originalEmail !== currentEmail && originalEmail.length > 0;

  const getQualityScore = useCallback((): number => {
    if (!originalEmail || !currentEmail) return 100;
    if (originalEmail === currentEmail) return 100;

    // Use word-level comparison
    const originalWords = tokenize(originalEmail);
    const currentWords = tokenize(currentEmail);

    const wordDistance = levenshteinDistanceArray(originalWords, currentWords);
    const maxWords = Math.max(originalWords.length, currentWords.length);

    // Check if all text was changed (complete rewrite)
    if (wordDistance >= maxWords) return 0;

    // Custom scoring based on number of words changed
    if (wordDistance === 0) return 100;
    if (wordDistance === 1) return 95;
    if (wordDistance <= 3) return 90;
    if (wordDistance <= 5) return 80;
    if (wordDistance <= 10) return 60;
    if (wordDistance <= 15) return 50;
    if (wordDistance <= 20) return 40;
    if (wordDistance <= 30) return 30;
    return 15; // More than 30 words changed
  }, [originalEmail, currentEmail]);

  const resetFeedbackState = useCallback(() => {
    setOriginalEmail("");
    setCurrentEmail("");
    setShowFeedbackPopup(false);
    setPendingNavigation(null);
  }, []);

  return (
    <EmailFeedbackContext.Provider
      value={{
        originalEmail,
        currentEmail,
        setOriginalEmail,
        setCurrentEmail,
        hasChanges,
        getQualityScore,
        showFeedbackPopup,
        setShowFeedbackPopup,
        pendingNavigation,
        setPendingNavigation,
        resetFeedbackState,
      }}
    >
      {children}
    </EmailFeedbackContext.Provider>
  );
}

export function useEmailFeedback() {
  const context = useContext(EmailFeedbackContext);
  if (!context) {
    throw new Error(
      "useEmailFeedback must be used within an EmailFeedbackProvider"
    );
  }
  return context;
}
